import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {map} from "rxjs/operators";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";
import {DatePipe} from "@angular/common";
import {SignatureConfig} from "../../modules/dashboard/home/home-enterprise/document/sign-flow-document/signature-config.model";
import {EnvService} from "./env.service";

const routes = {
  genImage: `/image/generate-image`
};

@Injectable({
  providedIn: 'root'
})
export class ImageGenerateService {

  constructor(private apiService: ApiService,
              private http: HttpClient,
              private translateService: TranslateService,
              private env: EnvService) {
  }

  /**
   * Áp cấu hình ảnh ký mặc định của chứng thư lên signConfig trước khi gen ảnh hệ thống:
   * - cert có defaultSignImageType = 0 (người dùng đã cấu hình mặc định cho phương thức ký đó):
   *   đọc defaultSignConfig trả về từ API lấy CTS (đã kèm ảnh ký nhúng dạng data URL do BE
   *   inject), ghi đè enabled/value lên signConfig; tên chủ thể luôn theo cert đang ký,
   *   ngày giờ ký luôn là thời điểm ký (không dùng giờ lưu trong config).
   * - cert chưa cấu hình (null) / config hỏng: giữ nguyên signConfig theo user-config, trả false.
   * phoneNumber không áp từ config — do từng màn ký tự quản theo cert đang chọn.
   */
  applyCertDefaultSignConfig(signConfig: SignatureConfig, cert: any): boolean {
    if (!cert || cert.defaultSignImageType !== 0 || !cert.defaultSignConfig) {
      return false;
    }
    try {
      const saved = JSON.parse(cert.defaultSignConfig);
      for (const key in signConfig) {
        if (key === 'phoneNumber') {
          continue;
        }
        if (saved[key] && typeof saved[key] === 'object') {
          signConfig[key].enabled = !!saved[key].enabled;
          if (saved[key].value !== undefined && saved[key].value !== null && saved[key].value !== '') {
            signConfig[key].value = saved[key].value;
          }
        } else if (saved[key] !== undefined && key === 'contentPosition') {
          signConfig[key].value = saved[key];
        } else {
          // field KHÔNG có trong config cert = TẮT: bắt buộc ghi đè enabled=false,
          // nếu không field đang bật theo user-config (địa chỉ, chức vụ...) sẽ lọt vào ảnh
          signConfig[key].enabled = false;
        }
      }
      const issueTo = cert.issuedTo || cert.certificateName;
      if (issueTo) {
        signConfig.enterpriseName.value = issueTo;
      }
      if (signConfig.signingTime && signConfig.signingTime.enabled) {
        signConfig.signingTime.value = new DatePipe('vi-VN').transform(new Date(), 'HH:mm dd/MM/yyyy');
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  genImage(signConfig: SignatureConfig, imageSize: { width: string, height: string }) {
    // return this.http.post('http://localhost:8080/api/image', { responseType: 'blob' });
    let width = imageSize.width.replace("px", "");
    let height = imageSize.height.replace("px", "");

    const payload: GenImagePayload = {
      imagePosition: signConfig.signaturePosition.value,
      contentPosition: signConfig.contentPosition.value,
      ratio: signConfig.signatureRatio.value.replace(":", ""),
      checked: false,
      width: Number(width),
      height: Number(height),
      fontName: signConfig.fontFamily.value,
      fontStyle: "",
      fontWeight: "",
      textColor: "",
      uid: "",
      textField: [{
        key: "",
        lineNumber: 1,
        content: ""
      }]
    };
    const textField: GenImageTextField[] = [];

    if (signConfig) {
      if (signConfig.enterpriseName.enabled) {
        textField.push({
          key: `${this.translateService.instant(signConfig.enterpriseName.label)}: `,
          lineNumber: 1,
          content: signConfig.enterpriseName.value
        })
      }

      if (signConfig.signingTime.enabled) {
        // field bật mà config chưa có giá trị (cert chưa cấu hình, giờ lấy từ user-config
        // chưa kịp về hoặc date API trả rỗng) -> điền giờ hiện tại NGAY VÀO signConfig
        // (ghi ngược lại để phần preview DOM của màn ký - render từ signConfig - hiện
        // giống ảnh API) rồi mới push vào payload — đúng ý nghĩa "giờ tại thời điểm ký",
        // tránh dòng "Ngày giờ ký:" trống
        if (!signConfig.signingTime.value) {
          signConfig.signingTime.value = new DatePipe('vi-VN').transform(new Date(), 'HH:mm dd/MM/yyyy');
        }
        textField.push({
          key: `${this.translateService.instant(signConfig.signingTime.label)}: `,
          lineNumber: 3,
          content: signConfig.signingTime.value
        })
      }
      if (signConfig.customSymbol && signConfig.customSymbol.enabled) {
        textField.push({
          key: ``,
          lineNumber: 3,
          content: signConfig.customSymbol.value
        })
      }

      if (signConfig.phoneNumber && signConfig.phoneNumber.enabled) {
        textField.push({
          key: `${this.translateService.instant(signConfig.phoneNumber.label)}: `,
          lineNumber: 3,
          content: signConfig.phoneNumber.value
        })
      }
      if (signConfig.position.enabled) {
        textField.push({
          key: `${this.translateService.instant(signConfig.position.label)}: `,
          lineNumber: 3,
          content: signConfig.position.value
        })
      }

      if (signConfig.address.enabled) {
        textField.push({
          key: `${this.translateService.instant(signConfig.address.label)}: `,
          lineNumber: 3,
          content: signConfig.address.value
        })
      }
      
      if (signConfig.organizationName && signConfig.organizationName.enabled) {
        textField.push({
          key: `${this.translateService.instant(signConfig.organizationName.label)}: `,
          lineNumber: 3,
          content: signConfig.organizationName.value
        })
      }

      // add field email to payload request
      if (signConfig.email.enabled) {
        textField.push({
          key: `${this.translateService.instant(signConfig.email.label)}: `,
          lineNumber: 3,
          content: signConfig.email.value
        })
      }
      if (signConfig.confirmBy.enabled) {
        textField.push({
          key: `${this.translateService.instant(signConfig.confirmBy.label)}: `,
          lineNumber: 3,
          content: signConfig.confirmBy.value
        })
      }


      payload.fontStyle = signConfig.fontStyle.value;
      payload.fontWeight = signConfig.fontWeight.value;

      if (signConfig.greenTick.enabled) {
        payload.checked = true;
      }

      if (signConfig.signatureImage.enabled && signConfig.signatureImageBase64.value) {
        payload.image = signConfig.signatureImageBase64.value;
      }
    }

    payload.textField = textField;
    let url = routes.genImage;
    let isCustomUrl = false;
    if (this.env.genimage_api_base_url) {
      url = `${this.env.genimage_api_base_url}${routes.genImage}`;
      isCustomUrl = true;
    }

    return this.apiService.postFileBase64(url, payload, {
      responseType: 'blob',
      headers: new HttpHeaders({'X-Silent-Loading': '1'})
    }, isCustomUrl)
      .pipe(map(res => {
        return res
      }));
  }

}

export interface GenImageTextField {
  key: string,
  lineNumber: number
  content: string
}

export interface GenImagePayload {
  image?: string,
  imagePosition: string,
  contentPosition: string,
  ratio: string,
  checked: boolean
  width: number
  height: number
  fontName: string
  fontStyle: string,
  fontWeight: string,
  textColor: string,
  uid: string
  textField: GenImageTextField[]
}
